// src/api.js
import axios from "axios";

/*
  Main POS API:
    Local   -> http://localhost/server.php
    Online  -> https://cozybnb.kendiservers.com/server.php

  Hotel API:
    Local   -> http://localhost/hotel_server.php
    Online  -> https://cozybnb.kendiservers.com/hotel_server.php

  Optional overrides:
    localStorage.setItem("api_base", "http://localhost/server.php");
    localStorage.setItem("hotel_api_base", "http://localhost/hotel_server.php");
*/

function normalize(u) {
  return String(u || "").trim().replace(/\/+$/, "");
}

function isLocalHost() {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
}

export function computeApiBase() {
  const stored = localStorage.getItem("api_base");
  if (stored) return normalize(stored);

  if (isLocalHost()) {
    return "http://localhost/server.php";
  }

  return "https://cozybnb.kendiservers.com/server.php";
}

export function computeHotelApiBase() {
  const stored = localStorage.getItem("hotel_api_base");
  if (stored) return normalize(stored);

  if (isLocalHost()) {
    return "http://localhost/hotel_server.php";
  }

  return "https://cozybnb.kendiservers.com/hotel_server.php";
}

export const API_BASE = normalize(computeApiBase());
export const HOTEL_API_BASE = normalize(computeHotelApiBase());

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 20000,
});

export const hotelApi = axios.create({
  baseURL: HOTEL_API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 20000,
});

// Store header
export function setStoreIdHeader(storeId) {
  const sid = Number(storeId || localStorage.getItem("store_id") || 0);

  if (sid > 0) {
    api.defaults.headers.common["X-Store-ID"] = String(sid);
    hotelApi.defaults.headers.common["X-Store-ID"] = String(sid);
  } else {
    delete api.defaults.headers.common["X-Store-ID"];
    delete hotelApi.defaults.headers.common["X-Store-ID"];
  }
}

// User header
export function setUserHeader(username) {
  const u = String(
    username ||
      localStorage.getItem("username") ||
      localStorage.getItem("saved_username") ||
      localStorage.getItem("user") ||
      ""
  ).trim();

  if (u) {
    api.defaults.headers.common["X-User"] = u;
    hotelApi.defaults.headers.common["X-User"] = u;
  } else {
    delete api.defaults.headers.common["X-User"];
    delete hotelApi.defaults.headers.common["X-User"];
  }
}

// Initial sync
setStoreIdHeader();
setUserHeader();

console.log("[API] baseURL =", API_BASE);
console.log("[HOTEL API] baseURL =", HOTEL_API_BASE);

// Error interceptor (main API)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    console.error("[API ERROR]", {
      baseURL: err?.config?.baseURL || API_BASE,
      url: err?.config?.url,
      method: err?.config?.method,
      status: err?.response?.status,
      data: err?.response?.data,
    });
    return Promise.reject(err);
  }
);

// Error interceptor (hotel API)
hotelApi.interceptors.response.use(
  (r) => r,
  (err) => {
    console.error("[HOTEL API ERROR]", {
      baseURL: err?.config?.baseURL || HOTEL_API_BASE,
      url: err?.config?.url,
      method: err?.config?.method,
      status: err?.response?.status,
      data: err?.response?.data,
    });
    return Promise.reject(err);
  }
);

/* Optional helper wrappers */
export async function apiGet(url, config = {}) {
  const res = await api.get(url, config);
  return res.data;
}

export async function apiPost(url, data = {}, config = {}) {
  const res = await api.post(url, data, config);
  return res.data;
}

export async function apiPut(url, data = {}, config = {}) {
  const res = await api.put(url, data, config);
  return res.data;
}

export async function apiDelete(url, config = {}) {
  const res = await api.delete(url, config);
  return res.data;
}

export async function hotelGet(url, config = {}) {
  const res = await hotelApi.get(url, config);
  return res.data;
}

export async function hotelPost(url, data = {}, config = {}) {
  const res = await hotelApi.post(url, data, config);
  return res.data;
}

export async function hotelPut(url, data = {}, config = {}) {
  const res = await hotelApi.put(url, data, config);
  return res.data;
}

export async function hotelDelete(url, config = {}) {
  const res = await hotelApi.delete(url, config);
  return res.data;
}